import {BuyNowEvent} from '@udemy/browse-event-tracking';
import {ClientEvent, Tracker} from '@udemy/event-tracking';

export type CertVoucher = {
    name: string;
    examId: string | null;
    poolId: string;
    voucherId: string | null;
};

/** This event is fired when a user clicks on a certification exam link. */
export class CertificationVoucherExamLinkSelected extends ClientEvent {
    constructor({certVoucher}: {certVoucher: CertVoucher}) {
        super('CertificationVoucherExamLinkSelected');
        this.certVoucher = certVoucher;
    }
    certVoucher: CertVoucher;
}

export const trackClickExamLink = (certVoucher: CertVoucher) => {
    Tracker.publishEvent(
        new CertificationVoucherExamLinkSelected({
            certVoucher,
        }),
    );
};

/** This event is fired when a user selects a certification voucher offer. (Buy now button) */
export class CertificationVoucherOfferSelected extends ClientEvent {
    constructor({
        certVoucher,
        uiRegion,
        poolId,
    }: {
        certVoucher: CertVoucher;
        uiRegion: string;
        poolId: string | null;
    }) {
        super('CertificationVoucherOfferSelected');
        this.certVoucher = certVoucher;
        this.uiRegion = uiRegion;
        this.poolId = poolId;
    }

    certVoucher: CertVoucher;
    uiRegion: string;
    poolId: string | null;
}

/**
 * Track buy now click using both the standard BuyNowEvent and the existing CertificationVoucherOfferSelected
 * This provides consistency with other buyable types while maintaining existing tracking
 */
export const trackBuyNowClick = (
    examId: string,
    poolId: string,
    voucherTitle: string,
    priceServeTrackingId?: string,
) => {
    // Fire the existing custom event
    Tracker.publishEvent(
        new CertificationVoucherOfferSelected({
            certVoucher: {
                name: voucherTitle,
                examId: examId,
                poolId: poolId,
                voucherId: null,
            },
            uiRegion: 'voucher_landing',
            poolId: poolId,
        }),
    );

    // Fire the standard BuyNowEvent for consistency with other buyable types
    // Note: poolId is a string identifier, so we need to handle the case where it can't be parsed as a number
    const numericId = parseInt(poolId, 10);
    const finalId = isNaN(numericId) ? 0 : numericId; // Use 0 as default for non-numeric IDs

    Tracker.publishEvent(
        new BuyNowEvent({
            buyable: {
                id: finalId,
                type: 'certification_voucher',
                trackingId: priceServeTrackingId || '',
            },
        }),
    );
};

/** This event is fired when a user views a price impression. */
export class PriceImpressionEvent extends ClientEvent {
    constructor({
        buyableTrackingId,
        context,
        currency,
        listPrice,
        discountPrice,
        displayedPrice,
        priceType,
        buyableType,
        buyableId,
    }: {
        buyableTrackingId: string;
        context: string;
        currency: string;
        listPrice: string;
        discountPrice: string;
        displayedPrice: string;
        priceType: string;
        buyableType: string;
        buyableId: number;
    }) {
        super('PriceImpressionEvent');
        this.buyableTrackingId = buyableTrackingId;
        this.context = context;
        this.currency = currency;
        this.listPrice = listPrice;
        this.discountPrice = discountPrice;
        this.displayedPrice = displayedPrice;
        this.priceType = priceType;
        this.buyableType = buyableType;
        this.buyableId = buyableId;
    }

    buyableTrackingId: string;
    context: string;
    currency: string;
    listPrice: string;
    discountPrice: string;
    displayedPrice: string;
    priceType: string;
    buyableType: string;
    buyableId: number;
}

export const trackViewLandingPage = (
    listPrice: string,
    discountPrice: string,
    displayedPrice: string,
) => {
    Tracker.publishEvent(
        new PriceImpressionEvent({
            buyableTrackingId: '123',
            context: 'voucher-landing-page',
            currency: 'USD',
            listPrice,
            discountPrice,
            displayedPrice,
            priceType: 'buyable_price',
            buyableType: 'certification_voucher',
            buyableId: 123,
        }),
    );
};
